import BankConciliationAccountsCard from '@/components/Financial/BankConciliation/BankConciliationAccountsCard';
import BankConciliationConfirmModal from '@/components/Financial/BankConciliation/BankConciliationConfirmModal';
import BankConciliationTransactionsCard from '@/components/Financial/BankConciliation/BankConciliationTransactionsCard';
import ReadTransactionsFileModal from '@/components/Financial/BankConciliation/ReadTransactionsFileModal';
import { SaveButton, TabContentWrapper } from '@/components/_common';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import {
  useBankConciliationConciliate,
  useBankConciliationReadTransactions,
} from '@/core/domain/financial/BankConciliation/BankConciliation.hooks';
import {
  ReadTransactionsFileFormValues,
  TransactionData,
  TransactionsWithInstallment,
  TransactionType,
} from '@/core/domain/financial/BankConciliation/BankConciliation.types';
import { useDisclosure } from '@/hooks';
import { PageProps } from '@/types/PageProps';
import { Button, Col, Empty, Row } from 'antd';
import { createContext, useContext, useEffect, useState } from 'react';

type BankConciliationContextProps = {
  bankAccountId?: number;
  transactionType: TransactionType;
  setTransactionType: React.Dispatch<React.SetStateAction<TransactionType>>;
  isDebit: boolean;
  isCredit: boolean;
  installmentsIdsSelected?: number[];
  setInstallmentsIdsSelected: React.Dispatch<
    React.SetStateAction<number[] | undefined>
  >;
  transactionSelected?: TransactionData;
  setTransactionSelected: React.Dispatch<
    React.SetStateAction<TransactionData | undefined>
  >;
  conciliatesData?: TransactionData[];
  setConciliatesData: React.Dispatch<
    React.SetStateAction<TransactionData[] | undefined>
  >;
  transactionData: TransactionData[];
  setTransactionData: React.Dispatch<React.SetStateAction<TransactionData[]>>;
};

const BankConciliationContext = createContext(
  {} as BankConciliationContextProps,
);

export const useBankConciliationContext = () =>
  useContext(BankConciliationContext);

export default function BankConciliationPage({ route }: PageProps) {
  const [transactionType, setTransactionType] = useState<TransactionType>(
    TransactionType.credit,
  );

  const [installmentsIdsSelected, setInstallmentsIdsSelected] =
    useState<number[]>();
  const [transactionData, setTransactionData] = useState<TransactionData[]>([]);
  const [conciliatesData, setConciliatesData] = useState<TransactionData[]>();
  const [transactionSelected, setTransactionSelected] =
    useState<TransactionData>();

  const isCredit = transactionType === TransactionType.credit;
  const isDebit = transactionType === TransactionType.debit;

  const [uploadFileOpened, uploadFileActions] = useDisclosure();

  const [handleConciliate, handleConciliateAction] = useDisclosure(false);

  const readTransaction = useBankConciliationReadTransactions();

  const conciliate = useBankConciliationConciliate();

  useEffect(() => {
    if (conciliatesData) {
      const allInstallmentIds = conciliatesData.flatMap((transaction) =>
        transaction.installments.map(
          (installment) => installment.installmentId,
        ),
      );

      setInstallmentsIdsSelected(allInstallmentIds);
    }
  }, [conciliatesData]);

  async function handleReadArchive(values?: ReadTransactionsFileFormValues) {
    if (!values) return;

    const response = await readTransaction.mutateAsync({
      file: values.file.originFileObj as File,
      bankAccountId: values.bankAccountId,
    });

    const installments = response.transactionsWithInstallments?.filter(
      ({ installmentPayables, installmentReceivables }) =>
        installmentPayables.length > 0 || installmentReceivables.length > 0,
    );

    const transformData = ({
      transaction,
      installmentPayables,
      installmentReceivables,
    }: TransactionsWithInstallment) => ({
      type: transaction.type,
      value: transaction.value,
      name: transaction.name,
      payDate: transaction.payDate,
      transactionId: transaction.transactionId,
      installments: [...installmentPayables, ...installmentReceivables].map(
        (installment) => ({
          ...installment,
          isChecked: true,
        }),
      ),
    });

    setConciliatesData(installments?.map(transformData));
    setTransactionData(
      response.transactionsWithInstallments?.map(transformData),
    );
  }
  return (
    <BankConciliationContext.Provider
      value={{
        transactionType,
        setTransactionType,
        bankAccountId: readTransaction.data?.bankAccountId,
        isDebit,
        isCredit,
        installmentsIdsSelected,
        setInstallmentsIdsSelected,
        transactionSelected,
        setTransactionSelected,
        transactionData,
        setTransactionData,
        conciliatesData,
        setConciliatesData,
      }}
    >
      <TabContentWrapper
        permissions={route.permissions}
        title={route.name}
        extra={
          <SpaceForButton>
            <Button type="primary" onClick={uploadFileActions.open}>
              Adicionar anexo (.OFX)
            </Button>

            <SaveButton
              block
              type="primary"
              loading={conciliate.isLoading}
              onClick={handleConciliateAction.open}
            >
              Conciliar
            </SaveButton>
          </SpaceForButton>
        }
      >
        <Row gutter={[12, 12]}>
          {readTransaction.data?.transactionsWithInstallments?.length ? (
            <>
              <Col span={24} md={12}>
                <BankConciliationTransactionsCard />
              </Col>

              <Col span={24} md={12}>
                <BankConciliationAccountsCard />
              </Col>
            </>
          ) : (
            <Col span={24}>
              <Empty
                description={
                  'Adicione seu arquivo clicando no botão "Adicionar Anexo (.OFX)" na parte superior direita'
                }
              />
            </Col>
          )}
        </Row>

        <ReadTransactionsFileModal
          onSubmit={handleReadArchive}
          open={uploadFileOpened}
          onClose={uploadFileActions.close}
        />

        <BankConciliationConfirmModal
          open={handleConciliate}
          onCancel={handleConciliateAction.close}
          onOk={() => {
            readTransaction.reset();
          }}
        />
      </TabContentWrapper>
    </BankConciliationContext.Provider>
  );
}
